/**
 * **Mieteinheiten eines Gebäudes** — aus ganzen Etagen oder einzelnen Räumen zusammengesetzt
 * (tri-server docs/metering.md).
 *
 * - Fläche: Summe der Raumflächen, ohne Räume die Etagen-BGF; die vertragliche Fläche bleibt daneben.
 * - Mietverträge: Mieter, Vermieter, Laufzeit und Miete je Einheit (aus dem Datenraum übernommen oder angelegt).
 * - Zähler: Die Einheit zeigt ihre Zähler (zugeordnet im Reiter „Zähler“) und den Verbrauch je Medium.
 *   Gezählt werden nur ihre obersten Zähler, ein Unterzähler in derselben Einheit nicht doppelt.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { entityPath } from "./MeterHierarchyPanel";
import { Building2, DoorOpen, FileSignature, Layers, Plus, Save, Trash2 } from "lucide-react";
import { useModalContext } from "../../components/ModalProvider";
import { MEDIA, RentalLease, RentalUnit, RentalUnitsView, useMeteringAPI } from "../../MeteringAPI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

const num = (v: number | null | undefined, unit = "") =>
  v == null ? "—" : `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(v)}${unit ? ` ${unit}` : ""}`;

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "…");
/** Der Vertrag, den die Liste zeigt: laufend vor künftig vor beendet. */
const mainLease = (leases: RentalLease[] | undefined) =>
  [...(leases ?? [])].sort((a, b) => ["active", "upcoming", "ended"].indexOf(a.status) - ["active", "upcoming", "ended"].indexOf(b.status))[0];

type Draft = { _id?: string; name: string; floors: string[]; spaces: string[]; grossFloorArea: string };

export default function RentalUnitsPanel({ building }: { building: any }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useMeteringAPI();
  const { pushError } = useModalContext() as any;
  const buildingId = String(building?._id ?? "");
  const [view, setView] = useState<RentalUnitsView | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setView(await api.rentalUnits(buildingId));
    } catch (error: any) {
      pushError(error);
    }
  };
  useEffect(() => {
    if (buildingId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId]);

  /** Wo Etage oder Raum schon vergeben ist (andere Einheit), damit nichts doppelt vermietet wird. */
  const takenBy = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of view?.rentalUnits ?? []) {
      if (u._id === draft?._id) continue;
      for (const id of [...u.floors, ...u.spaces]) map.set(id, u.name);
    }
    return map;
  }, [view, draft?._id]);

  const edit = (u?: RentalUnit) =>
    setDraft(u ? { _id: u._id, name: u.name, floors: [...u.floors], spaces: [...u.spaces], grossFloorArea: u.grossFloorArea == null ? "" : String(u.grossFloorArea) } : { name: "", floors: [], spaces: [], grossFloorArea: "" });

  const toggleFloor = (floorId: string, on: boolean) => {
    if (!draft || !view) return;
    const rooms = view.spaces.filter(s => s.floor === floorId).map(s => s._id);
    setDraft({
      ...draft,
      floors: on ? Array.from(new Set([...draft.floors, floorId])) : draft.floors.filter(f => f !== floorId),
      // Ganze Etage: einzelne Räume dieser Etage sind darin enthalten.
      spaces: draft.spaces.filter(s => !rooms.includes(s)),
    });
  };
  const toggleSpace = (spaceId: string, on: boolean) => {
    if (!draft) return;
    setDraft({ ...draft, spaces: on ? Array.from(new Set([...draft.spaces, spaceId])) : draft.spaces.filter(s => s !== spaceId) });
  };

  const draftArea = useMemo(() => {
    if (!draft || !view) return null;
    let total = 0;
    let known = false;
    for (const f of draft.floors) {
      const rooms = view.spaces.filter(s => s.floor === f && typeof s.floorArea === "number");
      const floor = view.floors.find(x => x._id === f);
      if (rooms.length) {
        total += rooms.reduce((sum, s) => sum + (s.floorArea as number), 0);
        known = true;
      } else if (typeof floor?.grossFloorArea === "number") {
        total += floor.grossFloorArea;
        known = true;
      }
    }
    for (const id of draft.spaces) {
      const s = view.spaces.find(x => x._id === id);
      if (typeof s?.floorArea === "number") {
        total += s.floorArea;
        known = true;
      }
    }
    return known ? total : null;
  }, [draft, view]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const saved = await api.saveRentalUnit(buildingId, { name: draft.name, floors: draft.floors, spaces: draft.spaces, grossFloorArea: draft.grossFloorArea === "" ? null : Number(draft.grossFloorArea) } as any, draft._id);
      await load();
      setDraft({ ...draft, _id: saved._id });
    } catch (error: any) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!draft?._id) return;
    try {
      await api.deleteRentalUnit(buildingId, draft._id);
      setDraft(null);
      await load();
    } catch (error: any) {
      pushError(error);
    }
  };

  if (!view) return <Skeleton className="h-64 w-full" />;
  const current = view.rentalUnits.find(u => u._id === draft?._id);

  return (
    <div className="grid min-w-0 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]" data-testid="rental-units">
      <div className="flex min-w-0 flex-col gap-2">
        <Button size="sm" className="self-start" onClick={() => edit()}>
          <Plus className="mr-1 h-4 w-4" /> {t("rentalUnits.add")}
        </Button>
        {view.rentalUnits.length ? (
          view.rentalUnits.map(u => (
            <button
              key={u._id}
              type="button"
              onClick={() => edit(u)}
              className={`flex min-w-0 flex-col gap-1 rounded-md border p-3 text-left text-sm ${draft?._id === u._id ? "border-primary bg-primary/5" : "hover:bg-muted"}`}
            >
              <span className="flex items-center gap-2 font-semibold">
                <Building2 aria-hidden className="h-4 w-4 shrink-0" />
                <span className="min-w-0 truncate">{u.name}</span>
              </span>
              <span className="text-xs text-muted-foreground">
                {t("rentalUnits.summary", { floors: u.floors.length, spaces: u.spaces.length, meters: u.meters.length })} · {num(u.computedArea ?? u.grossFloorArea, "m²")}
              </span>
              {mainLease(u.leases) ? (
                <span className="flex min-w-0 items-center gap-1 text-xs">
                  <FileSignature aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 truncate font-medium">{mainLease(u.leases)!.tenant?.name ?? t("rentalUnits.unknownTenant")}</span>
                  <span className="shrink-0 text-muted-foreground">
                    {t(`rentalUnits.leaseStatus.${mainLease(u.leases)!.status}`)} · {day(mainLease(u.leases)!.startDate)}–{day(mainLease(u.leases)!.endDate)}
                  </span>
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">{t("rentalUnits.vacant")}</span>
              )}
              <span className="flex flex-wrap gap-1">
                {MEDIA.filter(m => u.consumption[m] != null).map(m => (
                  <Badge key={m} variant="secondary" className="text-[10px] tabular-nums">
                    {t(`metering.medium.${m}`)} {num(u.consumption[m], view.units[m])}
                  </Badge>
                ))}
              </span>
            </button>
          ))
        ) : (
          <p className="text-sm text-muted-foreground">{t("rentalUnits.empty")}</p>
        )}
      </div>

      {draft ? (
        <div className="flex min-w-0 flex-col gap-3 rounded-md border p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              {t("rentalUnits.name")}
              <Input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder={t("rentalUnits.namePlaceholder")} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              {t("rentalUnits.contractArea")}
              <Input type="number" min={0} value={draft.grossFloorArea} onChange={e => setDraft({ ...draft, grossFloorArea: e.target.value })} />
            </label>
          </div>
          <div className="text-sm">
            {t("rentalUnits.computedArea")}: <span className="font-semibold tabular-nums">{num(draftArea, "m²")}</span>
          </div>
          <div className="min-w-0 rounded-md border p-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("rentalUnits.composition")}</div>
            {view.floors.length ? (
              view.floors.map(f => {
                const rooms = view.spaces.filter(s => s.floor === f._id);
                const whole = draft.floors.includes(f._id);
                return (
                  <div key={f._id} className="py-1">
                    <label className="flex items-center gap-2 text-sm">
                      <Checkbox checked={whole} disabled={takenBy.has(f._id)} onCheckedChange={(v: boolean | "indeterminate") => toggleFloor(f._id, v === true)} />
                      <Layers aria-hidden className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{f.name}</span>
                      <span className="text-xs text-muted-foreground">{t("rentalUnits.wholeFloor")}</span>
                      {takenBy.has(f._id) ? <span className="text-xs text-amber-700">{t("rentalUnits.takenBy", { name: takenBy.get(f._id) })}</span> : null}
                    </label>
                    {rooms.length ? (
                      <div className="ml-7 mt-1 grid gap-1 sm:grid-cols-2">
                        {rooms.map(s => (
                          <label key={s._id} className="flex min-w-0 items-center gap-2 text-sm">
                            <Checkbox
                              checked={whole || draft.spaces.includes(s._id)}
                              disabled={whole || takenBy.has(s._id)}
                              onCheckedChange={(v: boolean | "indeterminate") => toggleSpace(s._id, v === true)}
                            />
                            <DoorOpen aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="min-w-0 truncate">{s.name}</span>
                            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{num(s.floorArea, "m²")}</span>
                            {takenBy.has(s._id) ? <span className="shrink-0 text-xs text-amber-700">{takenBy.get(s._id)}</span> : null}
                          </label>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-muted-foreground">{t("rentalUnits.noFloors")}</p>
            )}
          </div>
          {current ? (
            <div className="min-w-0 text-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("rentalUnits.leases")}</div>
              {current.leases?.length ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="text-xs text-muted-foreground">
                      <tr>
                        <th className="py-1 pr-3 font-medium">{t("rentalUnits.tenant")}</th>
                        <th className="py-1 pr-3 font-medium">{t("rentalUnits.landlord")}</th>
                        <th className="py-1 pr-3 font-medium">{t("rentalUnits.term")}</th>
                        <th className="py-1 pr-3 text-right font-medium">{t("rentalUnits.rent")}</th>
                        <th className="py-1 font-medium">{t("rentalUnits.contract")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.leases.map(l => (
                        <tr key={l._id} className="border-t align-top">
                          <td className="py-1 pr-3">
                            {l.tenant ? (
                              <button type="button" className="text-left text-primary underline" onClick={() => navigate(entityPath("LegalPerson", l.tenant!._id))}>
                                {l.tenant.name ?? t("rentalUnits.unknownTenant")}
                              </button>
                            ) : (
                              "—"
                            )}
                            <div className="text-xs text-muted-foreground">{t(`rentalUnits.leaseStatus.${l.status}`)}</div>
                          </td>
                          <td className="py-1 pr-3">{l.landlord?.name ?? "—"}</td>
                          <td className="whitespace-nowrap py-1 pr-3 tabular-nums">
                            {day(l.startDate)}–{day(l.endDate)}
                          </td>
                          <td className="whitespace-nowrap py-1 pr-3 text-right tabular-nums">
                            {l.rent?.amount != null ? `${num(l.rent.amount)} ${l.rent.currency === "EUR" || !l.rent.currency ? "€" : l.rent.currency}` : "—"}
                          </td>
                          <td className="py-1">
                            <button type="button" className="text-left text-primary underline" onClick={() => navigate(entityPath("Lease", l._id))}>
                              {l.contractNumber ?? t("rentalUnits.openLease")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted-foreground">{t("rentalUnits.noLeases")}</p>
              )}
            </div>
          ) : null}
          {current ? (
            <div className="text-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("rentalUnits.meters")}</div>
              {current.meters.length ? (
                <ul className="list-disc pl-5">
                  {current.meters.map(m => (
                    <li key={m._id}>
                      <button type="button" className="text-left text-primary underline" onClick={() => navigate(entityPath("Asset", m._id))}>
                        {m.name}
                      </button>{" "}
                      <span className="text-xs text-muted-foreground">({t(`metering.medium.${m.medium}`)}{m.top ? "" : `, ${t("rentalUnits.subMeter")}`})</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">{t("rentalUnits.noMeters")}</p>
              )}
            </div>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            {draft._id ? (
              <Button variant="outline" onClick={remove}>
                <Trash2 className="mr-1 h-4 w-4" /> {t("rentalUnits.delete")}
              </Button>
            ) : null}
            <Button onClick={save} disabled={saving || !draft.name.trim()}>
              <Save className="mr-1 h-4 w-4" /> {t("rentalUnits.save")}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t("rentalUnits.pick")}</p>
      )}
    </div>
  );
}
